<script lang="ts" setup>
import { ResponsiveMap, CompactType, LayoutItem } from '@Shared/components/GridLayout/types';
import { useScreen } from '@Shared/utils/useScreen';
import GridLayout from '@Shared/components/GridLayout/GridLayout.vue';
import { CustomerCentreStageDataSource } from '@Shared/types/portal/dashboard';
import { buildUrlForImage } from '@Shared/utils/util';

const props = defineProps<{
  dataSource: CustomerCentreStageDataSource;
}>();

const isLogoImageBroken = ref(false);

const compactRules = CompactType.Horizontal | CompactType.PreservingVisualOrder | CompactType.Vertical;

const breakpoints = {
  sm: 480,
};

const layout = computed<ResponsiveMap<typeof breakpoints, LayoutItem[]> | null>(() => ({
  sm: props.dataSource.bottomLayoutDetails.map((layoutItem, i) => ({
    key: layoutItem.visual,
    x: props.dataSource.bottomLayoutDetails
      .slice(0, i)
      .map((layoutItem) => layoutItem.width)
      .reduce((x, acc) => x + acc, 0),
    y: 0,
    h: 1,
    w: layoutItem.width,
  })),
}));

const screen = useScreen();
const margin = computed(() => {
  let x = 40;
  if (screen.value.width >= 3840) {
    x = 128;
  } else if (screen.value.width >= 1920) {
    x = 64;
  }

  return {
    x,
    y: 0,
  };
});

const backgroundUrl = computed(() => {
  return "url('" + buildUrlForImage('background') + "')";
});
const clientLogo = computed(() => {
  return "url('" + buildUrlForImage('logo') + "')";
});
const handleBrokenImage = () => {
  isLogoImageBroken.value = true;
};
</script>

<style>
.background-image {
  background-image: v-bind(backgroundUrl), url(/ccs/background/Background.svg);
}

@media screen and (min-width: 1536px) {
  .background-image {
    background-image: v-bind(backgroundUrl), url(/ccs/background/Background_2xl.svg);
  }
}

@media screen and (min-width: 3840px) {
  .background-image {
    background-image: v-bind(backgroundUrl), url(/ccs/background/Background_3xl.svg);
  }
}

.logo-image {
  background-image: v-bind(clientLogo);
}
</style>

<template>
  <div
    class="flex flex-col w-screen h-screen overflow-hidden background-image"
    style="background-repeat: no-repeat; background-size: cover"
  >
    <div class="flex flex-col mt-4 ml-11 mr-11 3xl:ml-22 3xl:mr-22">
      <div
        style="background-position-x: center"
        class="bg-url[/ccs/Logo_White.svg] h-16 3xl:h-48 bg-no-repeat bg-contain flex-[0_1_auto]"
      >
        <div
          v-if="!isLogoImageBroken"
          class="logo-image bg-center left-8 2xl:left-12 h-full w-[114px] 2xl:w-[180px] 3xl:w-[480px] bg-no-repeat bg-contain flex-[0_1_auto] float-left"
          @error="handleBrokenImage"
        ></div>
        <div
          class="bg-url[/resonate/Logo_White.svg] bg-center right-8 2xl:right-12 h-full w-[114px] 2xl:w-[180px] 3xl:w-[480px] bg-no-repeat bg-contain flex-[0_1_auto] float-right"
        />
      </div>
      <span class="text-white text-sm xl:text-3 2xl:text-4.5 3xl:text-9 uppercase font-semibold pl-6 mt-[10px]">
        MOST RECENT FEEDBACK
      </span>
      <slot class="flex-[1_1_auto]" name="TopVisual" id="TopVisual"></slot>
    </div>
    <div
      class="inline-flex items-center h-8 2xl:h-10 3xl:h-24 3xl:pl-8 3xl:pr-8 pl-17 flex-[0_1_auto] bg-[#CCD1D8] 2xl:pl-17"
    >
      <span
        class="text-[#4F4F4F] text-sm xl:text-3 2xl:text-5 3xl:text-9 uppercase font-semibold pt-2 2xl:pt-4 3xl:pt-8 3xl:pl-25"
      >
        {{ dataSource.bottomVisualsDateFilter }}
      </span>
    </div>
    <div
      class="flex-[1.5_1_auto] 2xl:flex-[1_1_auto] 3xl:flex-[1.5_1_auto] pt-2 bg-[#CCD1D8] pl-11 pr-11 3xl:pl-25 3xl:pr-25 pb-6.5 3xl:pt-8 2xl:pl-10 2xl:pt-4 2xl:pb-6 2xl:pr-10 3xl:pb-12"
    >
      <GridLayout
        v-if="layout"
        :breakpoints="{ sm: 480 }"
        :container-padding="{ x: 0, y: 0 }"
        :cols="12"
        :margin="margin"
        use-css-transforms
        :compact-rules="compactRules"
        :row-height="120"
        v-model="layout"
      >
        <template v-for="item in layout.sm" :key="item.key" #[item.key]>
          <slot :name="item.key"></slot>
        </template>
      </GridLayout>
    </div>
  </div>
</template>
